import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import type { ColDef } from "ag-grid-community";
import { useNavigate, useParams } from "react-router-dom";
import {
  AgGridTable,
  LoadingState,
  InputNumber,
  SearchableSelect,
} from "components";
import { ArrowLeft, Plus, Save, Trash2 } from "lucide-react";
import { useCustomer, useSaleOrder, useWmsItem } from "hooks";
import { SaleOrder, SaleOrderForm, SaleOrderItem } from "types";
import SnackbarUtils from "utils/SnackbarUtils";
import { ROUTES } from "utils";
import { Controller, useForm } from "react-hook-form";
import {
  normalizeDateInput,
  parseNumberSafe as normalizeNumber,
} from "utils/helpers/accounting";
import { sanitizeSaleOrderItems } from "utils/helpers/sanitizers";

const getUnitString = (value: any): string => {
  if (!value) return "";
  if (typeof value === "string" || typeof value === "number")
    return String(value);
  if (typeof value === "object") {
    return value.name ?? value.code ?? "";
  }
  return "";
};

const todayString = () => new Date().toISOString().slice(0, 10);

type SaleOrderItemWithMeta = SaleOrderItem & { __tempId?: string };

const defaultItem: SaleOrderItemWithMeta = {
  product_id: "",
  wms_id: "",
  unit_id: "",
  product_name: "",
  unit: "",
  quantity: 1,
  price: 0,
  discount_rate: 0,
  other_reduction: 0,
  vat_rate: 0,
  converted_unit: "",
  conversion_type: "",
  conversion_rate: undefined,
};

const defaultFormValues: SaleOrderForm = {
  order_date: todayString(),
  due_date: todayString(),
  customer_id: "",
  buyer_name: "",
  buyer_tax_code: "",
  buyer_address: "",
  payment_method: "bank",
  invoice_type: "vat",
  invoice_number: "",
  invoice_date: todayString(),
  discount_rate: 0,
  vat_rate: 0,
  description: "",
  attachments: [],
  items: [],
};

const ensureTempIds = (
  list: SaleOrderItemWithMeta[],
): SaleOrderItemWithMeta[] => {
  return list.map((item) => {
    if (item.__tempId) return item;
    return {
      ...item,
      __tempId: `temp-${Math.random().toString(36).slice(2, 9)}`,
    };
  });
};

const getItemAmount = (item: SaleOrderItem): number => {
  const quantity = normalizeNumber(item.quantity);
  const price = normalizeNumber(item.price);
  const baseAmount = quantity * price;
  const discountRate =
    item.discount_rate !== undefined && item.discount_rate !== null
      ? Number(item.discount_rate)
      : 0;
  const discountAmount =
    discountRate > 0 ? (baseAmount * discountRate) / 100 : 0;
  const otherReduction =
    item.other_reduction !== undefined && item.other_reduction !== null
      ? normalizeNumber(item.other_reduction)
      : 0;
  if ((item as any).amount !== undefined && item.amount !== null) {
    return normalizeNumber((item as any).amount);
  }
  return Math.max(baseAmount - discountAmount - otherReduction, 0);
};

const getItemVatAmount = (item: SaleOrderItem): number => {
  const amount = getItemAmount(item);
  const vatRate = normalizeNumber(item.vat_rate);
  if ((item as any).vat_amount !== undefined && item.vat_amount !== null) {
    return normalizeNumber((item as any).vat_amount);
  }
  return Number.isFinite(vatRate) && vatRate > 0 ? (amount * vatRate) / 100 : 0;
};

const SaleOrderDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const itemIdRef = useRef(0);
  const { fetchDetail, createSaleOrder, updateSaleOrder } = useSaleOrder();
  const { data: customersData } = useCustomer().fetchList(
    { page: 1, pageSize: 200 },
    true,
  );
  const { fetchList: fetchWmsItems } = useWmsItem();
  const { data: wmsItems = [] } = fetchWmsItems(true);

  const { data: detailResponse, isFetching: isFetchingDetail } = fetchDetail(
    id ?? "",
    isEdit,
  );

  const {
    control,
    register,
    handleSubmit: formHandleSubmit,
    reset,
    setValue,
    getValues,
  } = useForm<SaleOrderForm>({
    defaultValues: defaultFormValues,
  });
  const [items, setItems] = useState<SaleOrderItemWithMeta[]>(() =>
    ensureTempIds([defaultItem]),
  );

  useEffect(() => {
    if (isEdit) return;
    reset(defaultFormValues);
    setItems(ensureTempIds([defaultItem]));
  }, [isEdit, reset]);

  const customerOptions =
    (customersData?.data as any[])?.map((c) => ({
      value: c.id,
      label: `${c.code}${c.name ? ` - ${c.name}` : ""}`,
      address: c.address,
      name: c.name,
      tax_code: c.tax_code,
    })) ?? [];

  const wmsItemOptions = useMemo(
    () =>
      (wmsItems ?? []).map((item: any) => ({
        value: item.id,
        label: `${item.code}${item.name ? ` - ${item.name}` : ""}`,
        name: item.name,
        unit: getUnitString(item.unit),
        unit_id: item.unit_id ?? item.unit?.id,
        wms_id: item.wms_id,
      })),
    [wmsItems],
  );

  const wmsItemById = useMemo(() => {
    const map = new Map<number | string, any>();
    (wmsItems ?? []).forEach((item: any) => {
      map.set(item.id, item);
      map.set(String(item.id), item);
    });
    return map;
  }, [wmsItems]);

  const attachProductInfo = useCallback(
    (list: SaleOrderItemWithMeta[]) =>
      list.map((item) => {
        const selected =
          wmsItemById.get(item.product_id) || wmsItemById.get(item.wms_id);
        if (!selected) return item;
        return {
          ...item,
          product_name: item.product_name || selected.name || "",
          unit: item.unit || getUnitString(selected.unit) || "",
          unit_id:
            item.unit_id ??
            selected.unit_id ??
            (selected.unit ? selected.unit.id : undefined),
          wms_id:
            item.wms_id ??
            selected.wms_id ??
            (selected.id !== undefined ? selected.id : undefined),
        };
      }),
    [wmsItemById],
  );
  useEffect(() => {
    if (!detailResponse?.data) return;
    const saleOrder = detailResponse.data as SaleOrder;
    const sanitizedItems = attachProductInfo(
      ensureTempIds(
        sanitizeSaleOrderItems(saleOrder.items) as SaleOrderItemWithMeta[],
      ),
    );
    const orderDate = normalizeDateInput(saleOrder.order_date) || todayString();
    const dueDate =
      normalizeDateInput(saleOrder.due_date) ||
      normalizeDateInput(saleOrder.invoice_date) ||
      orderDate;
    const invoiceDate =
      normalizeDateInput(saleOrder.invoice_date) ||
      normalizeDateInput(saleOrder.order_date) ||
      orderDate;

    reset({
      order_date: orderDate,
      due_date: dueDate,
      customer_id: saleOrder.customer_id ?? "",
      buyer_name: saleOrder.buyer_name ?? "",
      buyer_tax_code: saleOrder.buyer_tax_code ?? "",
      buyer_address: saleOrder.buyer_address ?? "",
      payment_method: saleOrder.payment_method ?? "bank",
      invoice_type: saleOrder.invoice_type ?? "vat",
      invoice_number: saleOrder.invoice_number ?? "",
      invoice_date: invoiceDate,
      discount_rate: saleOrder.discount_rate ?? 0,
      vat_rate: saleOrder.vat_rate ?? 0,
      description: saleOrder.description ?? "",
      attachments: saleOrder.attachments ?? [],
      items: sanitizedItems,
    });
    setItems(
      sanitizedItems.length ? sanitizedItems : ensureTempIds([defaultItem]),
    );
  }, [attachProductInfo, detailResponse, reset]);

  useEffect(() => {
    if (!isEdit) return;
    setItems((prev) => attachProductInfo(prev));
  }, [attachProductInfo, isEdit]);
  const totalAmount = useMemo(() => {
    return items.reduce(
      (sum, item) => sum + getItemAmount(item) + getItemVatAmount(item),
      0,
    );
  }, [items]);

  const handleAddItem = useCallback(() => {
    setItems((prev) => {
      const nextId = `temp-${itemIdRef.current++}`;
      return [...prev, { ...defaultItem, __tempId: nextId }];
    });
  }, []);

  const handleItemChange = useCallback(
    (index: number, key: keyof SaleOrderItem, value: any) => {
      setItems((prev) =>
        prev.map((item, idx) =>
          idx === index
            ? {
                ...item,
                [key]:
                  key === "quantity" ||
                  key === "price" ||
                  key === "vat_rate" ||
                  key === "discount_rate" ||
                  key === "other_reduction" ||
                  key === "conversion_rate" ||
                  key === "converted_quantity" ||
                  key === "converted_price"
                    ? normalizeNumber(value)
                    : key === "product_id" ||
                        key === "wms_id" ||
                        key === "unit_id"
                      ? Number.isFinite(Number(value))
                        ? Number(value)
                        : value
                      : value,
              }
            : item,
        ),
      );
    },
    [],
  );

  const handleSelectProduct = useCallback(
    (index: number, value: any) => {
      setItems((prev) =>
        prev.map((item, idx) => {
          if (idx !== index) return item;
          if (!value) {
            return {
              ...item,
              product_id: "",
              product_name: "",
              unit: "",
              unit_id: "",
              wms_id: undefined,
            };
          }
          const selected =
            wmsItemById.get(Number(value)) || wmsItemById.get(String(value));
          return {
            ...item,
            product_id: Number.isFinite(Number(value)) ? Number(value) : value,
            product_name: selected?.name ?? "",
            unit: getUnitString(selected?.unit) || "",
            unit_id:
              selected?.unit_id !== undefined && selected?.unit_id !== null
                ? selected.unit_id
                : selected?.unit?.id,
            wms_id:
              selected?.wms_id !== undefined && selected?.wms_id !== null
                ? selected.wms_id
                : selected?.id,
          };
        }),
      );
    },
    [wmsItemById],
  );

  const handleRemoveItem = useCallback((index: number) => {
    setItems((prev) => prev.filter((_, idx) => idx !== index));
  }, []);

  const buildPayload = useCallback(
    (data: SaleOrderForm): SaleOrderForm | null => {
      const orderDate = normalizeDateInput(data.order_date) || todayString();
      if (!orderDate || !data.customer_id) {
        SnackbarUtils.error("Ngày bán và Khách hàng là bắt buộc");
        return null;
      }

      const normalizedItems = sanitizeSaleOrderItems(
        items,
      ) as SaleOrderItemWithMeta[];
      if (!normalizedItems.length) {
        SnackbarUtils.error("Vui lòng nhập ít nhất một dòng hàng hóa");
        return null;
      }

      const attachments = Array.isArray(data.attachments)
        ? data.attachments
            .map((att) => String(att).trim())
            .filter((att) => Boolean(att))
        : [];

      const buyerTaxCode = data.buyer_tax_code?.trim();
      const invoiceNumber = data.invoice_number?.trim();
      const description = data.description?.trim();
      const dueDate =
        normalizeDateInput(data.due_date) ||
        normalizeDateInput(data.invoice_date) ||
        orderDate;
      const invoiceDate =
        normalizeDateInput(data.invoice_date) ||
        normalizeDateInput(data.order_date) ||
        orderDate;

      return {
        order_date: orderDate,
        due_date: dueDate || orderDate,
        customer_id: data.customer_id,
        buyer_name: data.buyer_name?.trim() || "",
        buyer_tax_code: buyerTaxCode || undefined,
        buyer_address: data.buyer_address?.trim() || "",
        payment_method: data.payment_method,
        invoice_type: data.invoice_type,
        invoice_number: invoiceNumber || undefined,
        invoice_date: invoiceDate || orderDate,
        discount_rate: data.discount_rate ?? 0,
        vat_rate: data.vat_rate ?? 0,
        description: description ?? "",
        attachments,
        items: normalizedItems.map((item) => ({
          ...(item.id ? { id: item.id } : {}),
          product_id: item.product_id,
          wms_id: item.wms_id,
          unit_id: item.unit_id,
          quantity: item.quantity,
          price: item.price,
          discount_rate: item.discount_rate,
          other_reduction: item.other_reduction,
          vat_rate: item.vat_rate,
          converted_unit: item.converted_unit
            ? String(item.converted_unit).trim()
            : undefined,
          conversion_type: item.conversion_type || undefined,
          conversion_rate:
            item.conversion_rate !== undefined
              ? item.conversion_rate
              : undefined,
        })),
      };
    },
    [items],
  );

  const onSubmit = useCallback(
    (data: SaleOrderForm) => {
      const payload = buildPayload(data);
      if (!payload) return;

      if (isEdit && id) {
        updateSaleOrder.mutate(
          { id, ...payload },
          {
            onSuccess: () => {
              navigate(ROUTES.ACCOUNTING.SALES_DOCUMENT);
            },
          },
        );
        return;
      }

      createSaleOrder.mutate(payload, {
        onSuccess: () => {
          navigate(ROUTES.ACCOUNTING.SALES_DOCUMENT);
        },
      });
    },
    [buildPayload, createSaleOrder, id, isEdit, navigate, updateSaleOrder],
  );

  const formSubmit = useCallback(
    (e?: React.FormEvent) => {
      e?.preventDefault();

      // Explicit validation before form submission
      const formData = getValues();
      if (!formData.order_date || !formData.customer_id) {
        SnackbarUtils.error("Ngày bán và Khách hàng là bắt buộc");
        return;
      }

      // Call react-hook-form's handleSubmit
      formHandleSubmit(onSubmit)(e);
    },
    [formHandleSubmit, getValues, onSubmit],
  );

  const isSaving = createSaleOrder.isPending || updateSaleOrder.isPending;

  // Keyboard shortcuts: Ctrl+S save, Ctrl+N add item
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        if (!isSaving && !isFetchingDetail) {
          formSubmit();
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "n") {
        e.preventDefault();
        if (!isSaving && !isFetchingDetail) {
          handleAddItem();
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [formSubmit, handleAddItem, isSaving, isFetchingDetail]);

  const suppressGridKeyboard = useCallback((params: any) => {
    const event = params?.event as KeyboardEvent | undefined;
    if (!event) return false;
    const target = event.target as HTMLElement | null;
    if (!target) return false;
    const tag = target.tagName;
    if (
      (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA") &&
      ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)
    ) {
      event.stopPropagation?.();
      return true;
    }
    return false;
  }, []);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: false,
      filter: false,
      floatingFilter: false,
      resizable: true,
      editable: false,
      minWidth: 120,
      cellClass: "!border-transparent",
      suppressKeyboardEvent: suppressGridKeyboard,
    }),
    [suppressGridKeyboard],
  );

  const columns = useMemo<ColDef<SaleOrderItem & { __actions: any }>[]>(() => {
    const renderNumberInput = (
      params: any,
      key: keyof SaleOrderItem,
      opts?: { suffix?: string; decimalScale?: number },
    ) => {
      const rowIndex = params.node?.rowIndex ?? 0;
      return (
        <InputNumber
          value={normalizeNumber((params.data as SaleOrderItem)?.[key])}
          onValueChange={(values) =>
            handleItemChange(rowIndex, key, values.floatValue ?? 0)
          }
          className="h-9 w-full px-2 py-1 text-right text-sm"
          suffix={opts?.suffix}
          decimalScale={opts?.decimalScale ?? 0}
        />
      );
    };

    return [
      {
        headerName: "Hàng hóa",
        field: "product_id",
        minWidth: 220,
        flex: 1.1,
        editable: false,
        pinned: "left",
        cellRenderer: (params) => {
          const rowIndex = params.node?.rowIndex ?? 0;
          return (
            <select
              value={(params.data as SaleOrderItem)?.product_id ?? ""}
              onChange={(e) => handleSelectProduct(rowIndex, e.target.value)}
              className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
            >
              <option value="">Chọn hàng hóa</option>
              {wmsItemOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          );
        },
      },
      {
        headerName: "Tên hàng",
        field: "product_name",
        minWidth: 200,
        flex: 1.1,
        editable: false,
        cellRenderer: (params) => (
          <span className="text-gray-900">
            {(params.data as SaleOrderItem)?.product_name ?? ""}
          </span>
        ),
      },
      {
        headerName: "Đơn vị",
        field: "unit",
        minWidth: 140,
        flex: 0.8,
        editable: false,
        cellRenderer: (params) => {
          const data = params.data as SaleOrderItem;
          const unitLabel =
            getUnitString((data as any)?.unit) ||
            (data?.converted_unit ? String(data.converted_unit) : "") ||
            (data?.unit_id ? String(data.unit_id) : "");
          return <span className="text-gray-900">{unitLabel}</span>;
        },
      },
      {
        headerName: "SL",
        field: "quantity",
        minWidth: 140,
        flex: 0.8,
        cellRenderer: (params) =>
          renderNumberInput(params, "quantity", { decimalScale: 2 }),
      },
      {
        headerName: "Đơn giá",
        field: "price",
        minWidth: 150,
        flex: 0.9,
        cellRenderer: (params) =>
          renderNumberInput(params, "price", {
            decimalScale: 2,
            suffix: " ₫",
          }),
      },
      {
        headerName: "Chiết khấu %",
        field: "discount_rate",
        minWidth: 150,
        flex: 0.9,
        cellRenderer: (params) =>
          renderNumberInput(params, "discount_rate", {
            decimalScale: 2,
            suffix: "%",
          }),
      },
      {
        headerName: "Giảm khác",
        field: "other_reduction",
        minWidth: 150,
        flex: 0.9,
        cellRenderer: (params) =>
          renderNumberInput(params, "other_reduction", {
            decimalScale: 0,
            suffix: " ₫",
          }),
      },
      {
        headerName: "VAT %",
        field: "vat_rate",
        minWidth: 140,
        flex: 0.8,
        cellRenderer: (params) =>
          renderNumberInput(params, "vat_rate", {
            decimalScale: 2,
            suffix: "%",
          }),
      },
      {
        headerName: "Đơn vị quy đổi",
        field: "converted_unit",
        minWidth: 180,
        flex: 1,
        cellRenderer: (params) => {
          const rowIndex = params.node?.rowIndex ?? 0;
          return (
            <input
              value={(params.data as SaleOrderItem)?.converted_unit ?? ""}
              onChange={(e) =>
                handleItemChange(rowIndex, "converted_unit", e.target.value)
              }
              className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
              placeholder="Thùng"
            />
          );
        },
      },
      {
        headerName: "Kiểu quy đổi",
        field: "conversion_type",
        minWidth: 150,
        flex: 0.9,
        cellRenderer: (params) => {
          const rowIndex = params.node?.rowIndex ?? 0;
          return (
            <select
              value={(params.data as SaleOrderItem)?.conversion_type ?? ""}
              onChange={(e) =>
                handleItemChange(rowIndex, "conversion_type", e.target.value)
              }
              className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
            >
              <option value="">Chọn kiểu</option>
              <option value="multiply">Nhân</option>
              <option value="divide">Chia</option>
            </select>
          );
        },
      },
      {
        headerName: "Tỷ lệ quy đổi",
        field: "conversion_rate",
        minWidth: 150,
        flex: 0.9,
        cellRenderer: (params) =>
          renderNumberInput(params, "conversion_rate", {
            decimalScale: 4,
          }),
      },
      {
        headerName: "Thành tiền",
        field: "amount",
        minWidth: 150,
        flex: 1,
        editable: false,
        valueGetter: (params) => getItemAmount(params.data as SaleOrderItem),
        valueFormatter: (params) =>
          `${normalizeNumber(params.value).toLocaleString("vi-VN")} ₫`,
        cellClass: "text-right text-gray-900 font-semibold",
      },
      {
        headerName: "Thuế VAT",
        field: "vat_amount",
        minWidth: 150,
        flex: 1,
        editable: false,
        valueGetter: (params) => getItemVatAmount(params.data as SaleOrderItem),
        valueFormatter: (params) =>
          `${normalizeNumber(params.value).toLocaleString("vi-VN")} ₫`,
        cellClass: "text-right text-gray-900 font-semibold",
      },
      {
        headerName: "Thao tác",
        field: "__actions",
        width: 130,
        editable: false,
        cellRenderer: (params) => {
          const rowIndex = params.node?.rowIndex ?? 0;
          return (
            <div className="flex items-center justify-center h-full">
              <button
                type="button"
                onClick={() => handleRemoveItem(rowIndex)}
                className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-700 disabled:opacity-60"
                disabled={items.length === 1}
              >
                <Trash2 className="h-4 w-4" />
                Xóa
              </button>
            </div>
          );
        },
      },
    ];
  }, [
    handleItemChange,
    handleRemoveItem,
    handleSelectProduct,
    items.length,
    wmsItemOptions,
  ]);

  const getItemRowId = useCallback((params: any) => {
    const data = params.data as SaleOrderItemWithMeta;
    if (data?.id !== undefined && data?.id !== null) return String(data.id);
    if (data?.__tempId) return data.__tempId;
    return String(params.rowIndex ?? params.node?.rowIndex ?? Math.random());
  }, []);

  return (
    <>
      {(isFetchingDetail || isSaving) && (
        <LoadingState
          message={isEdit ? "Đang tải đơn bán hàng" : "Đang lưu đơn bán hàng"}
          description="Vui lòng chờ trong giây lát."
        />
      )}
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500 uppercase">Đơn bán hàng</p>
            <h1 className="text-2xl font-semibold text-gray-900">
              {isEdit ? "Cập nhật đơn bán hàng" : "Thêm đơn bán hàng"}
            </h1>
            <p className="text-sm text-gray-600">
              Nhập thông tin chung và danh sách hàng hóa như mẫu.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(ROUTES.ACCOUNTING.SALES_DOCUMENT)}
              className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <ArrowLeft className="h-4 w-4" />
              Danh sách đơn bán
            </button>
            <button
              type="button"
              onClick={formSubmit}
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700 disabled:opacity-70"
              disabled={isSaving}
            >
              <Save className="h-4 w-4" />
              {isSaving ? "Đang lưu..." : "Lưu đơn"}
            </button>
          </div>
        </div>

        <form onSubmit={formSubmit} className="space-y-6">
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-200 px-4 py-3">
              <h2 className="text-base font-semibold text-gray-900">
                Thông tin chung
              </h2>
            </div>
            <div className="grid gap-4 px-4 py-4 md:grid-cols-3">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Ngày bán *
                </label>
                <input
                  type="date"
                  {...register("order_date", { required: true })}
                  className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Ngày đến hạn
                </label>
                <input
                  type="date"
                  {...register("due_date")}
                  className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Ngày hóa đơn
                </label>
                <input
                  type="date"
                  {...register("invoice_date")}
                  className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Khách hàng *
                </label>
                <Controller
                  control={control}
                  name="customer_id"
                  rules={{ required: true }}
                  render={({ field }) => (
                    <SearchableSelect
                      options={customerOptions.map((opt) => ({
                        value: opt.value,
                        label: opt.label,
                        searchData: opt.name,
                      }))}
                      value={field.value}
                      onChange={(value) => {
                        const selectedValue = value ? Number(value) : "";
                        field.onChange(selectedValue);
                        const selected = customerOptions.find(
                          (opt) => String(opt.value) === String(value),
                        );
                        if (selected) {
                          if (!getValues("buyer_name")) {
                            setValue("buyer_name", selected.name ?? "", {
                              shouldDirty: true,
                            });
                          }
                          if (!getValues("buyer_address") && selected.address) {
                            setValue("buyer_address", selected.address, {
                              shouldDirty: true,
                            });
                          }
                          if (
                            !getValues("buyer_tax_code") &&
                            selected.tax_code
                          ) {
                            setValue("buyer_tax_code", selected.tax_code, {
                              shouldDirty: true,
                            });
                          }
                        }
                      }}
                      placeholder="Tìm kiếm khách hàng..."
                      clearable
                    />
                  )}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Phương thức thanh toán
                </label>
                <select
                  {...register("payment_method")}
                  className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                >
                  <option value="cash">Tiền mặt</option>
                  <option value="bank">Ngân hàng</option>
                  <option value="debt">Công nợ</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Người mua
                </label>
                <input
                  {...register("buyer_name")}
                  className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="Công ty ABC"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Mã số thuế người mua
                </label>
                <input
                  {...register("buyer_tax_code")}
                  className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="0312345678"
                />
              </div>
              <div className="space-y-2 md:col-span-3">
                <label className="text-sm font-medium text-gray-700">
                  Địa chỉ
                </label>
                <input
                  {...register("buyer_address")}
                  className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="Hà Nội"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Loại hóa đơn
                </label>
                <select
                  {...register("invoice_type")}
                  className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                >
                  <option value="no_invoice">Không xuất hóa đơn</option>
                  <option value="vat">Hóa đơn VAT</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Số hóa đơn
                </label>
                <input
                  {...register("invoice_number")}
                  className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="0000123"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Tỷ lệ chiết khấu (%)
                </label>
                <input
                  type="number"
                  {...register("discount_rate", {
                    setValueAs: (v) => (v === "" || v === null ? 0 : Number(v)),
                  })}
                  className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="5"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  VAT chung (%)
                </label>
                <input
                  type="number"
                  {...register("vat_rate", {
                    setValueAs: (v) => (v === "" || v === null ? 0 : Number(v)),
                  })}
                  className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="10"
                />
              </div>
              <div className="space-y-2 md:col-span-3">
                <label className="text-sm font-medium text-gray-700">
                  Đính kèm (mỗi dòng một tệp)
                </label>
                <Controller
                  control={control}
                  name="attachments"
                  render={({ field }) => (
                    <textarea
                      value={(field.value ?? []).join("\n")}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value
                            .split(/,/)
                            .map((item) => item.trim())
                            .filter(Boolean),
                        )
                      }
                      rows={2}
                      className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                      placeholder="https://example.com/file.pdf"
                    />
                  )}
                />
              </div>
              <div className="space-y-2 md:col-span-3">
                <label className="text-sm font-medium text-gray-700">
                  Ghi chú
                </label>
                <textarea
                  {...register("description")}
                  rows={2}
                  className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="Bán hàng tháng 12"
                />
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <h2 className="text-base font-semibold text-gray-900">
                Danh sách hàng hóa
              </h2>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-xs text-gray-500">
                    Tổng cộng (đã gồm VAT)
                  </p>
                  <p className="text-base font-semibold text-gray-900">
                    {totalAmount.toLocaleString("vi-VN")} đ
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleAddItem}
                  className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <Plus className="h-4 w-4" />
                  Thêm dòng
                </button>
              </div>
            </div>

            <div className="px-2 py-3 sale-order-items-grid">
              <AgGridTable
                rows={items}
                columns={columns}
                pagination={false}
                defaultColDef={defaultColDef}
                pinnedLeftKeys={["product_id"]}
                immutableData
                deltaRowDataMode
                getRowId={getItemRowId}
              />
            </div>
            <div className="flex items-center justify-between px-4 py-3">
              <p className="text-sm text-gray-500">
                Tổng cộng (đã gồm VAT):{" "}
                <span className="font-semibold text-gray-900">
                  {totalAmount.toLocaleString("vi-VN")} đ
                </span>
              </p>
              <button
                type="button"
                onClick={formSubmit}
                className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700 disabled:opacity-70"
                disabled={isSaving}
              >
                <Save className="h-4 w-4" />
                {isSaving ? "Đang lưu..." : "Lưu đơn"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default SaleOrderDetail;
